import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { AuthState } from "../types/admin";
import { authApi } from "../services/api";

interface AuthContextType {
  auth: AuthState;
  login: (token: string) => void;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  auth: { isAuthenticated: false, token: null },
  login: () => {},
  logout: () => {},
  isLoading: true,
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [auth, setAuth] = useState<AuthState>({
    isAuthenticated: false,
    token: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      authApi
        .validate()
        .then((res) => {
          if (res.success && res.data?.valid) {
            setAuth({ isAuthenticated: true, token });
          } else {
            localStorage.removeItem("admin_token");
          }
        })
        .catch(() => {
          localStorage.removeItem("admin_token");
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback((token: string) => {
    localStorage.setItem("admin_token", token);
    setAuth({ isAuthenticated: true, token });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("admin_token");
    setAuth({ isAuthenticated: false, token: null });
  }, []);

  return (
    <AuthContext.Provider value={{ auth, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => useContext(AuthContext);
